import type { UnsubscribeSuccessPayload } from "../types/unsubscribe-success-payload";

const base64UrlToBytes = (value: string): Uint8Array => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const constantTimeEqual = (left: Uint8Array, right: Uint8Array): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  let difference = 0;

  for (let i = 0; i < left.length; i += 1) {
    // Intentional constant-time compare: avoid short-circuiting on the first mismatch.
    // eslint-disable-next-line no-bitwise -- required for timing-safe equality
    difference |= (left[i] ?? 0) ^ (right[i] ?? 0);
  }

  return difference === 0;
};

const hmacSha256 = async (message: string, signingKey: string): Promise<Uint8Array> => {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(signingKey),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(message));

  return new Uint8Array(signature);
};

const isUnsubscribeSuccessPayload = (value: unknown): value is UnsubscribeSuccessPayload => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const payload = value as Record<string, unknown>;

  return (
    typeof payload.websiteName === "string" &&
    typeof payload.email === "string" &&
    typeof payload.notificationType === "string"
  );
};

const decodeUnsubscribePayload = async (
  urlPayload: string,
  signingKey: string,
): Promise<UnsubscribeSuccessPayload | null> => {
  const parts = urlPayload.split(".");

  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return null;
  }

  const [encodedJson, encodedSignature] = parts;

  try {
    const receivedSignature = base64UrlToBytes(encodedSignature);
    const expectedSignature = await hmacSha256(encodedJson, signingKey);

    if (!constantTimeEqual(receivedSignature, expectedSignature)) {
      return null;
    }

    const json = new TextDecoder().decode(base64UrlToBytes(encodedJson));
    const payload: unknown = JSON.parse(json);

    if (!isUnsubscribeSuccessPayload(payload)) {
      return null;
    }

    return {
      websiteName: payload.websiteName,
      email: payload.email,
      notificationType: payload.notificationType,
    };
  } catch {
    return null;
  }
};

export { decodeUnsubscribePayload };
